/**
 * useAmbientSound — manages ambient soundscapes by time of day.
 * Starts/stops ambient loops when time changes or settings toggle.
 * Use in ChildLayout only.
 */

import { useEffect, useRef } from 'react';
import { SoundEngine } from '../audio';
import { useSoundSettings } from './useSoundSettings';
import { useTimeOfDay, type TimeOfDay } from './useTimeOfDay';

export function useAmbientSound() {
  const time = useTimeOfDay();
  const { ambientEnabled, ambientVolume, effectivelyMuted } = useSoundSettings();
  const activeTimeRef = useRef<TimeOfDay | null>(null);

  useEffect(() => {
    const engine = SoundEngine.getInstance();

    if (!ambientEnabled || effectivelyMuted()) {
      engine.stopAmbient();
      activeTimeRef.current = null;
      return;
    }

    // Update volume
    engine.setAmbientVolume(ambientVolume);

    // Only restart if time-of-day changed
    if (activeTimeRef.current === time) return;
    activeTimeRef.current = time;

    switch (time) {
      case 'morning':
        engine.startMorningAmbient();
        break;
      case 'afternoon':
        engine.startAfternoonAmbient();
        break;
      case 'evening':
        engine.startEveningAmbient();
        break;
    }

    return () => {
      engine.stopAmbient();
      activeTimeRef.current = null;
    };
  }, [time, ambientEnabled, ambientVolume, effectivelyMuted]);
}
